import React, { useContext, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Box, Checkbox, Tooltip, Typography } from '@mui/material';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { chaveDeOrdenacao, estadoDaColuna } from '../../utils/ticketListSorting';

const CHECKBOX_W = 44;

/**
 * Listagem de tickets virtualizada com SCROLL INFINITO (react-virtuoso).
 *
 * O DataGrid community força paginação (100/página) e não permite scroll
 * infinito. Aqui renderizamos só as linhas visíveis (virtualização), com
 * cabeçalho fixo, e disparamos `onEndReached` ao chegar no fim para carregar
 * mais tickets sob demanda. As colunas são as MESMAS do filtro (GridColDef):
 * reaproveitamos `renderCell`, `headerName`, `flex` e `minWidth`.
 */
const TicketListVirtuoso = ({
    rows = [],
    columns = [],
    selectedIds = [],
    onSelectionChange,
    onRowClick,
    onEndReached,
    // Reporta a faixa visível {startIndex,endIndex} p/ o header mostrar "de X a Y" (item 9).
    onRangeChange,
    // Ordenação em vigor ({by, dir}) ou null = a ordem padrão da tela.
    sort = null,
    onSortChange,
    // Quando a lista está refinada só no navegador (busca avançada, painel por
    // ids, atalho de indicador), o servidor não sabe reproduzir esse recorte:
    // ordenar ali trocaria a lista por outra. O cabeçalho fica inerte e diz por quê.
    sortDisabledReason = null,
}) => {
    const { theme } = useContext(ThemeContext);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allSelected = rows.length > 0 && rows.every((r) => selectedSet.has(r.id));
    const someSelected = !allSelected && rows.some((r) => selectedSet.has(r.id));

    const toggleAll = () => {
        if (allSelected) onSelectionChange?.([]);
        else onSelectionChange?.(rows.map((r) => r.id));
    };
    const toggleOne = (id) => {
        const next = new Set(selectedSet);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onSelectionChange?.([...next]);
    };

    const cellSx = (col) => ({
        flex: col.flex != null ? `${col.flex} 1 0` : (col.width ? `0 0 ${col.width}px` : '1 1 0'),
        minWidth: col.minWidth || 0,
        px: 1,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
    });

    const borderColor = theme.palette.divider;

    const aoClicar = (col) => {
        const chave = chaveDeOrdenacao(col.field);

        if (!chave || sortDisabledReason || !onSortChange) return;

        onSortChange(chave);
    };

    /**
     * Uma célula do cabeçalho.
     *
     * A seta só aparece na coluna que está ordenando: uma seta apagada em toda
     * coluna ordenável polui o cabeçalho e some no meio das que não ordenam.
     * Quem descobre que dá para clicar é o cursor e o realce ao passar o mouse.
     */
    const renderizarCabecalho = (col) => {
        const { ordenavel, ativa, direcao } = estadoDaColuna(sort, col.field);
        const clicavel = ordenavel && !sortDisabledReason && Boolean(onSortChange);

        const conteudo = (
            <Box
                onClick={() => aoClicar(col)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    minWidth: 0,
                    borderRadius: 1,
                    px: 0.5,
                    mx: -0.5,
                    cursor: clicavel ? 'pointer' : 'default',
                    userSelect: 'none',
                    '&:hover': clicavel ? { backgroundColor: theme.palette.action.hover } : undefined,
                }}
            >
                <Typography
                    variant="caption"
                    fontWeight={700}
                    noWrap
                    sx={{ color: ativa ? theme.palette.text.primary : theme.palette.text.secondary }}
                >
                    {col.headerName}
                </Typography>
                {ativa && (direcao === 'asc'
                    ? <ArrowUpward sx={{ fontSize: 14, color: theme.palette.text.primary }} />
                    : <ArrowDownward sx={{ fontSize: 14, color: theme.palette.text.primary }} />)}
            </Box>
        );

        return (
            <Box key={col.field} sx={cellSx(col)}>
                {ordenavel && sortDisabledReason
                    ? <Tooltip title={sortDisabledReason} arrow placement="bottom">{conteudo}</Tooltip>
                    : conteudo}
            </Box>
        );
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Cabeçalho fixo */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: theme.palette.background.paper,
                    borderBottom: `1px solid ${borderColor}`,
                    minHeight: 44,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ width: CHECKBOX_W, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                    <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
                </Box>
                {columns.map((col) => renderizarCabecalho(col))}
            </Box>

            {/* Linhas virtualizadas + scroll infinito */}
            <Virtuoso
                style={{ flex: 1, minHeight: 0 }}
                data={rows}
                endReached={onEndReached}
                rangeChanged={onRangeChange}
                increaseViewportBy={400}
                itemContent={(index, ticket) => (
                    <Box
                        onClick={() => onRowClick?.(ticket)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: 42,
                            borderBottom: `1px solid ${borderColor}`,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: theme.palette.action.hover },
                        }}
                    >
                        <Box
                            sx={{ width: CHECKBOX_W, display: 'flex', justifyContent: 'center', flexShrink: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Checkbox size="small" checked={selectedSet.has(ticket.id)} onChange={() => toggleOne(ticket.id)} />
                        </Box>
                        {columns.map((col) => (
                            <Box
                                key={col.field}
                                sx={cellSx(col)}
                                onClick={col.field === 'actions' ? (e) => e.stopPropagation() : undefined}
                            >
                                {col.renderCell
                                    ? col.renderCell({ row: ticket, value: ticket[col.field], field: col.field, id: ticket.id, api: {} })
                                    : (ticket[col.field] ?? '')}
                            </Box>
                        ))}
                    </Box>
                )}
            />
        </Box>
    );
};

export default TicketListVirtuoso;
